import { $isAutoLinkNode, $isLinkNode, AutoLinkNode, LinkNode } from "@lexical/link";
import { $isListItemNode, $isListNode, ListItemNode, ListNode } from "@lexical/list";
import {
  $isLineBreakNode,
  $isTabNode,
  $isTextNode,
  LineBreakNode,
  ParagraphNode,
  TabNode,
  TextNode,
} from "lexical";
// import type, not inline type specifiers: esbuild keeps the latter as a side effect import,
// which would pull slack-blocks-to-jsx, and so React, into the api worker.
import type {
  RichTextBlock,
  RichTextList,
  RichTextSection,
  RichTextSectionEmoji,
  RichTextSectionLink,
  RichTextSectionText,
} from "slack-blocks-to-jsx";
import { ALLOWED_CHAT_NODES } from "../../../../moment/types/validators";
import { $isBroadcastNode } from "../../nodes/broadcast-node";
import { $isChannelNode } from "../../nodes/channel/channel-node";
import { $isCustomEmojiNode, CustomEmojiNode } from "../../nodes/custom-emoji/custom-emoji-node";
import { $isEmojiNode, EmojiNode } from "../../nodes/emoji-node";
import { $isMergeTagV2Node, MergeTagV2Node } from "../../nodes/merge-tag-node-v2";

export const SLACK_INVISIBLE_CHARACTER = "‎"; // Invisible character to ensure the block is not empty

export function convertSlackRichTextParagraph(node: ParagraphNode): RichTextBlock | null {
  const sections = convertToSections({ type: "paragraph", node });
  if (sections.length === 0) return null;

  const lastSection = sections[sections.length - 1];
  // Append \n\n to the last section if it exists
  if (lastSection && lastSection.elements.length > 0) {
    lastSection.elements.push({
      type: "text",
      text: "\n\n",
    });
  }

  return {
    type: "rich_text",
    elements: sections,
  };
}

export function convertSlackRichTextList(node: ListNode): RichTextBlock[] {
  const lists = convertListNode(node).filter((list) => list.elements.length > 0);

  if (lists.length === 0) return [];

  return [
    {
      type: "rich_text",
      elements: lists,
    },
  ];
}

export function createSlackRichText(
  text = SLACK_INVISIBLE_CHARACTER,
  style: { bold: boolean; italic: boolean } = { bold: false, italic: false },
): RichTextBlock {
  return {
    type: "rich_text",
    elements: [
      {
        type: "rich_text_section",
        elements: [
          {
            type: "text",
            text,
            style,
          },
        ],
      },
    ],
  };
}

function convertListNode(node: ListNode, indent = 0): RichTextList[] {
  const blocks: RichTextList[] = [];

  let currentList: RichTextList | null = makeNewList(indent, node.getListType());

  function makeNewList(indent_: number, listType: string): RichTextList {
    return {
      type: "rich_text_list",
      style: listType === "number" ? "ordered" : "bullet",
      indent: Math.min(Math.max(indent_, 0), 8) as 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8,
      elements: [],
    };
  }

  for (const listItem of node.getChildren()) {
    if (!$isListItemNode(listItem)) continue;

    // Convert this item's content
    const sections = convertToSections({ type: "listitem", node: listItem });
    for (const section of sections) {
      if (section && section.elements.length > 0) {
        currentList?.elements.push(section);
      }
    }

    // Check for nested lists
    const nestedListNodes = listItem.getChildren().filter($isListNode);

    if (nestedListNodes.length > 0) {
      // We have nested lists.

      // Flush the current list so far (must not include children!)
      if (currentList && currentList.elements.length > 0) {
        blocks.push(currentList);
        currentList = makeNewList(indent, node.getListType());
      }

      for (const nested of nestedListNodes) {
        const nestedBlocks = convertListNode(nested, indent + 1);
        blocks.push(...nestedBlocks);
      }
    }
  }

  // Push any remaining top-level items collected
  if (currentList && currentList.elements.length > 0) {
    blocks.push(currentList);
  }

  return blocks;
}

function convertToSections(
  node: { type: "paragraph"; node: ParagraphNode } | { type: "listitem"; node: ListItemNode },
): RichTextSection[] {
  const sections: RichTextSection[] = [
    {
      type: "rich_text_section",
      elements: [],
    },
  ];

  node.node.getChildren().forEach((child, index) => {
    const currentSection = sections[sections.length - 1];
    if (!currentSection) throw new Error("No current section found");

    if (!ALLOWED_CHAT_NODES[node.type]?.includes(child.getType())) {
      throw new Error("Invalid node type");
    }

    if ($isEmojiNode(child) || $isCustomEmojiNode(child)) {
      currentSection.elements.push(convertSlackEmojiNode(child));
      return;
    }

    if ($isMergeTagV2Node(child)) {
      currentSection.elements.push(convertMergeTagNode(child));
      return;
    }

    if ($isLinkNode(child) || $isAutoLinkNode(child)) {
      currentSection.elements.push(convertLinkNode(child));
      return;
    }

    if ($isLineBreakNode(child) && index === 0) {
      sections.push({
        type: "rich_text_section",
        elements: [
          {
            type: "text",
            text: SLACK_INVISIBLE_CHARACTER,
          },
        ],
      });
      sections.push({
        type: "rich_text_section",
        elements: [],
      });
      return;
    }

    if ($isTextNode(child) || $isLineBreakNode(child) || $isTabNode(child)) {
      currentSection.elements.push(convertSlackTextBlock(child));
      return;
    }

    if ($isBroadcastNode(child)) {
      currentSection.elements.push({
        type: "broadcast",
        range: child.getRange(),
      });
    }

    if ($isChannelNode(child)) {
      currentSection.elements.push({
        type: "channel",
        channel_id: child.getChannelId(),
      });
    }
  });

  return sections.filter((section) => section.elements.length > 0);
}

function convertSlackTextBlock(node: TextNode | LineBreakNode | TabNode): RichTextSectionText {
  const style = $isTextNode(node)
    ? {
        bold: node.hasFormat("bold"),
        italic: node.hasFormat("italic"),
      }
    : undefined;

  return {
    type: "text",
    text: node.getTextContent(),
    style,
  };
}

function convertSlackEmojiNode(node: EmojiNode | CustomEmojiNode): RichTextSectionEmoji {
  return {
    type: "emoji",
    name: node.getEmojiId(),
  };
}

function convertMergeTagNode(node: MergeTagV2Node): RichTextSectionText {
  return {
    type: "text",
    text: `{{${node.getTextContent()}}}`,
    style: {
      bold: node.hasFormat("bold"),
      italic: node.hasFormat("italic"),
    },
  };
}

function convertLinkNode(node: LinkNode | AutoLinkNode): RichTextSectionLink {
  let text = "";
  node.getChildren().forEach((child) => {
    if ($isMergeTagV2Node(child)) {
      text += `{{${child.getTextContent()}}}`;
    } else {
      text += child.getTextContent();
    }
  });

  return {
    type: "link",
    text,
    url: node.getURL(),
  };
}
